import { ApolloProvider } from '@apollo/client';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { lazy, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { CountryRestrictedRoute } from './components/CountryRestrictedRoute';
import { Layout } from './components/layouts/Layout';
import { GraphQLClient } from './config/apollo';
import { localeKeys } from './config/i18next';
import { SIDEBAR_LINKS_DATA } from './constants/mon-profil';
import {
  ARTICLE_EMB,
  ARTICLE_MEDEIS,
  BIBLIOTHEQUE_SAVOIR,
  BRIQUE_DE_SAVOIR,
  ERREUR_TECHNIQUE,
  ESPACE_PRIVE,
  EVENEMENTS,
  FORMULAIRE_INSCRIPTION,
  GENERIC_CONFIRMATION_PAGE,
  HOMEPAGE_EMB,
  HOMEPAGE_MEDEIS,
  HOMEPAGE_PODCASTS,
  HOMEPAGE_SAGA_MICROBIOTES,
  INSCRIPTION_DOUBLON,
  NON_DISPONIBLE,
  PARCOURS_FORMATION,
  PARCOURS_FORMATION_RESULT,
  PLAN_DU_SITE,
  PREVIEW,
  PROFIL,
  TEST_ORIENTATION,
  TEST_ORIENTATION_RESULT
} from './constants/routing';
import { DidomiContextProvider } from './context/DidomiContextProvider';
import { UserContextProvider } from './context/UserContextProvider';
import { withSuspense } from './hoc/with-suspens';
import { CommonRichtextStyle } from './models/dynamic-zone/componentGeneriqueTexte';
import { GenericConfirmationPage } from './pages/GenericConfirmationPage';
import InscriptionDoublonPage from './pages/InscriptionDoublon';
import NotAvailableInCurrentLanguage from './pages/NotAvailableInCurrentLanguage';
import NotFoundPage from './pages/NotFoundPage';
import { PageError } from './pages/PageError';
import theme from './theme';
import { getBrowserLocale } from './utils/getBrowserLocale';
import { getRouteSlug, RouteKey } from './utils/routes';

/**
 * Scroll top on each route change
 */
const ScrollToTop = ({ children }: React.PropsWithChildren) => {
  const location = useLocation();
  useEffect(() => {
    globalThis.scrollTo(0, 0);
  }, [location]);
  return <>{children}</>;
};

const NavigateToLang = () => {
  const { pathname } = useLocation();
  const locale = getBrowserLocale();
  return <Navigate to={`/${locale}${pathname === '/' ? '' : pathname}`} replace />;
};

export type RoutePageProps = {
  locale: string;
  commonRichtextStyle?: CommonRichtextStyle;
  slug?: string;
};

const App = () => {
  return (
    <ApolloProvider client={GraphQLClient}>
      <UserContextProvider>
        <DidomiContextProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <ScrollToTop>
                <Routes>
                  {localeKeys.map((locale) => {
                    const withLazy = (pagePath: string) =>
                      withSuspense(
                        lazy(
                          () =>
                            import(`./pages/${pagePath}`) as Promise<{
                              default: ({ locale, slug }: RoutePageProps) => JSX.Element;
                            }>
                        ),
                        { locale }
                      );
                    const Profile = lazy(() => import('./pages/MonProfil'));

                    return (
                      <Route key={locale} path={locale} element={<Layout locale={locale} />}>
                        {/* PUBLIC */}
                        <Route index element={withLazy('HomepageDeconnecte')} />
                        <Route
                          path={getRouteSlug(locale, FORMULAIRE_INSCRIPTION)}
                          element={withLazy('FormulaireInscription')}
                        />
                        <Route path={getRouteSlug(locale, INSCRIPTION_DOUBLON)} element={<InscriptionDoublonPage />} />
                        <Route
                          path={getRouteSlug(locale, GENERIC_CONFIRMATION_PAGE)}
                          element={<GenericConfirmationPage />}
                        />
                        <Route path={getRouteSlug(locale, PLAN_DU_SITE)} element={withLazy('PlanDuSite')} />
                        <Route path={':slug/'} element={withLazy('PagePublique')} />
                        <Route path={`${HOMEPAGE_EMB}`} element={withLazy('HomepageEmb')} />
                        <Route path={`${ARTICLE_EMB}/:slug`} element={withLazy('ArticleEmbPage')} />
                        <Route path={HOMEPAGE_PODCASTS} element={withLazy('HomepagePodcast')} />
                        <Route path={`${HOMEPAGE_PODCASTS}/:slug`} element={withLazy('PodcastDetailPage')} />

                        {/* PREVIEW */}
                        <Route path={PREVIEW}>
                          <Route path={`${BRIQUE_DE_SAVOIR}/:slug`} element={withLazy('BriqueSavoirPreview')} />
                          <Route path={`${ARTICLE_EMB}/:slug`} element={withLazy('ArticleEmbPreview')} />
                        </Route>

                        {/* PRIVATE */}
                        <Route path={getRouteSlug(locale, ESPACE_PRIVE)}>
                          <Route index element={withLazy('HomepageConnecte')} />

                          {/* Singles */}
                          {SIDEBAR_LINKS_DATA.map(({ slug }) => {
                            return (
                              <Route
                                key={slug}
                                path={`${getRouteSlug(locale, PROFIL)}/${getRouteSlug(locale, slug as RouteKey)}`}
                                element={withSuspense(Profile, {
                                  locale,
                                  slug
                                })}
                              />
                            );
                          })}
                          <Route path={`${HOMEPAGE_MEDEIS}`} element={withLazy('HomepageMedeis')} />
                          <Route path={`${HOMEPAGE_SAGA_MICROBIOTES}`} element={withLazy('HomepageSagaMicrobiotes')} />

                          {/* Collections Lists */}
                          <Route
                            path={getRouteSlug(locale, PARCOURS_FORMATION)}
                            element={withLazy('NosParcoursFormation')}
                          />
                          <Route
                            path={getRouteSlug(locale, BIBLIOTHEQUE_SAVOIR)}
                            element={withLazy('BibliothequeSavoir')}
                          />

                          {/* Agenda — restreint aux pays France et DOM-TOM */}
                          <Route
                            element={
                              <CountryRestrictedRoute redirectTo={`/${locale}/${getRouteSlug(locale, ESPACE_PRIVE)}`} />
                            }
                          >
                            <Route path={getRouteSlug(locale, EVENEMENTS)} element={withLazy('Agenda')} />
                            <Route
                              path={`${getRouteSlug(locale, EVENEMENTS)}/:slug/`}
                              element={withLazy('Evenement')}
                            />
                          </Route>

                          {/* Collection Singles */}
                          <Route
                            path={`${getRouteSlug(locale, BRIQUE_DE_SAVOIR)}/:slug`}
                            element={withLazy('BriqueSavoirPage')}
                          />
                          <Route path={`${ARTICLE_MEDEIS}/:slug`} element={withLazy('ArticleMedeisPage')} />
                          <Route
                            path={`${getRouteSlug(locale, PARCOURS_FORMATION)}/:slug`}
                            element={withLazy('ParcoursFormationPage')}
                          />
                          <Route
                            path={`${getRouteSlug(locale, PARCOURS_FORMATION)}/:slug/${getRouteSlug(locale, PARCOURS_FORMATION_RESULT)}`}
                            element={withLazy('ParcoursFormationResult')}
                          />
                          <Route
                            path={`${getRouteSlug(locale, TEST_ORIENTATION)}/:thematique`}
                            element={withLazy('TestOrientation')}
                          />
                          <Route
                            path={`${getRouteSlug(locale, TEST_ORIENTATION)}/:thematique/${getRouteSlug(locale, TEST_ORIENTATION_RESULT)}`}
                            element={withLazy('TestOrientationResult')}
                          />
                          <Route path={HOMEPAGE_PODCASTS} element={withLazy('HomepagePodcast')} />
                          <Route path={`${HOMEPAGE_PODCASTS}/:slug`} element={withLazy('PodcastDetailPage')} />
                          <Route path={':slug/'} element={withLazy('PagePrivee')} />
                        </Route>

                        {/* Others */}
                        <Route
                          path={getRouteSlug(locale, NON_DISPONIBLE)}
                          element={<NotAvailableInCurrentLanguage />}
                        />
                        <Route path={getRouteSlug(locale, ERREUR_TECHNIQUE)} element={<PageError locale={locale} />} />
                        <Route path="*" element={<NotFoundPage />} />
                      </Route>
                    );
                  })}
                  <Route path="*" element={<NavigateToLang />} />
                </Routes>
              </ScrollToTop>
            </BrowserRouter>
          </ThemeProvider>
        </DidomiContextProvider>
      </UserContextProvider>
    </ApolloProvider>
  );
};

export default App;
